import type { GetUserAnalyticsAggregatedDataOutput } from "@/lib/ai/tools/get-user-analytics-aggregated-data/server";
import {
  expectOptionalArrayField,
  expectVisualizationObjectOutput,
} from "@/lib/ai/visualization/data/adapters/adapter-contract";
import {
  alignCompareTimeseries,
  buildDataset,
  buildFields,
  extractArray,
  isRecord,
  resolveCompareAlignmentMode,
  resolveCompareDateRanges,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import {
  buildCompareMetricFieldEntries,
  buildCompareTimeseriesRowsFromMetrics,
  buildMetricFieldEntries,
  buildTimeseriesRowsFromMetrics,
  constrainMetricFieldsToFamilies,
  hasCoRealizedMetricFamilyPair,
  resolveCompareDefaultMetricKeys,
  resolveMetricChartProfile,
  resolveRequestedMetricFamiliesWithLegacyFallback,
} from "@/lib/ai/visualization/data/adapters/metric-binding";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";
import {
  UA_COMPARE_TIMESERIES_METRICS,
  UA_TIMESERIES_METRICS,
} from "./aggregated-metrics";
import { buildUaGroupedDatasets } from "./grouped-datasets";

const UA_TIMESERIES_PROFILE: NonNullable<VizDataset["profile"]> = {
  datasetFamily: "ua.aggregated",
  variant: "timeseries",
  compareMode: "none",
  supportedViews: [
    "time-series-line",
    "time-series-area",
    "time-series-composed",
    "scatter-plot",
    "table",
  ],
};

const UA_COMPARE_PROFILE: NonNullable<VizDataset["profile"]> = {
  datasetFamily: "ua.aggregated",
  variant: "timeseriesCompare",
  compareMode: "period",
  supportedViews: [
    "time-series-line",
    "time-series-area",
    "time-series-composed",
    "scatter-plot",
    "table",
  ],
};

const UA_REQUESTED_METRIC_FAMILIES = {
  SESSION_COUNT: "sessionCount",
  UNIQUE_USER_COUNT: "uniqueUserCount",
  AVG_SESSION_DURATION: "avgSessionDuration",
  CONVERSION_RATE: "conversionRate",
  CONVERSION_COUNT: "conversionCount",
  SURF_RATE: "surfRate",
  SURF_COUNT: "surfCount",
  ENGAGEMENT_RATE: "engagementRate",
  ENGAGEMENT_COUNT: "engagementCount",
  PAGE_VIEW_EVENT_COUNT: "pageViewEventCount",
} as const;

const UA_LEGACY_DEPENDENCY_RULES = [
  {
    derivedMetric: "CONVERSION_RATE",
    dependencyMetrics: ["SESSION_COUNT", "CONVERSION_COUNT"],
  },
  {
    derivedMetric: "SURF_RATE",
    dependencyMetrics: ["SESSION_COUNT", "SURF_COUNT"],
  },
  {
    derivedMetric: "ENGAGEMENT_RATE",
    dependencyMetrics: ["SESSION_COUNT", "ENGAGEMENT_COUNT"],
  },
  {
    derivedMetric: "AVG_SESSION_DURATION",
    dependencyMetrics: ["SESSION_COUNT"],
  },
] as const;

/**
 * UA aggregated 出力を timeseries/grouped/compare dataset 群へ変換する。
 */
export function buildUaAggregatedDatasets(
  rawOutput: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  const output = expectVisualizationObjectOutput<
    Partial<GetUserAnalyticsAggregatedDataOutput>
  >({
    toolName: source.toolName,
    output: rawOutput,
  });

  expectOptionalArrayField(output, "aggregatedData", source.toolName);
  expectOptionalArrayField(output, "comparingAggregatedData", source.toolName);
  expectOptionalArrayField(output, "groupedData", source.toolName);
  expectOptionalArrayField(output, "comparingGroupedData", source.toolName);

  const datasets: VizDataset[] = [];
  const applied = isRecord(output.applied) ? output.applied : null;
  const requestedMetricFamilies =
    resolveRequestedMetricFamiliesWithLegacyFallback({
      expandedMetrics: applied?.metrics,
      requestedMetrics: applied?.requestedMetrics,
      metricFamilyByRequestKey: UA_REQUESTED_METRIC_FAMILIES,
      defaultMetricKeys: Object.keys(UA_REQUESTED_METRIC_FAMILIES),
      dependencyRules: UA_LEGACY_DEPENDENCY_RULES,
    });
  const { currentDateRange, comparingDateRange } = resolveCompareDateRanges({
    output,
  });
  const compareAlignmentMode = resolveCompareAlignmentMode({ output });

  const aggregated = extractArray(output, "aggregatedData");
  if (aggregated.length > 0) {
    const rows = buildTimeseriesRowsFromMetrics({
      items: aggregated,
      metrics: UA_TIMESERIES_METRICS,
    });

    if (rows.length > 0) {
      datasets.push(
        buildDataset({
          id: "ua-agg-timeseries",
          profile: UA_TIMESERIES_PROFILE,
          fields: buildFields([
            { key: "date", role: "x", type: "date" },
            ...buildMetricFieldEntries(UA_TIMESERIES_METRICS),
          ]),
          rows,
          defaults: { x: "date", y: ["sessionCount"] },
          source,
        }),
      );
    }
  }

  datasets.push(
    ...buildUaGroupedDatasets({
      output,
      source,
      requestedMetricFamilies,
    }),
  );

  const comparingAggregated = extractArray(output, "comparingAggregatedData");
  if (aggregated.length > 0 && comparingAggregated.length > 0) {
    const alignedRows = alignCompareTimeseries({
      currentItems: aggregated,
      comparingItems: comparingAggregated,
      dateKey: "date",
      alignmentMode: compareAlignmentMode,
      currentDateRange,
      comparingDateRange,
    });

    const rows = buildCompareTimeseriesRowsFromMetrics({
      alignedRows,
      metrics: UA_COMPARE_TIMESERIES_METRICS,
    });

    if (rows.length > 0) {
      const fields = constrainMetricFieldsToFamilies({
        fields: buildFields([
          { key: "date", role: "x", type: "date" },
          { key: "comparingDate", role: "dim", type: "date" },
          ...buildCompareMetricFieldEntries(UA_COMPARE_TIMESERIES_METRICS),
        ]),
        metricFamilies: requestedMetricFamilies,
      });
      const supportsScatterPlot = hasCoRealizedMetricFamilyPair({
        rows,
        fields,
      });
      datasets.push(
        buildDataset({
          id: "ua-agg-compare",
          profile: resolveMetricChartProfile({
            profile: UA_COMPARE_PROFILE,
            rows,
            fields,
            supportsScatterPlot,
          }),
          fields,
          rows,
          defaults: {
            x: "date",
            category: "date",
            y: resolveCompareDefaultMetricKeys({
              fields,
              rows,
              preferredMetricFamily: "sessionCount",
            }),
          },
          source,
        }),
      );
    }
  }

  return datasets;
}
